import { Injectable } from '@angular/core';

import { State, Action, StateContext } from '@ngxs/store';
import { tap, catchError,take } from 'rxjs/operators';

import { BcStateContext, BcStateForm, Spinner } from '@bayclubs/state';
import { BlockedSlotService, CourtBookingService, CourtClassService, CourtEventType } from '@bayclubs/court-booking-employee-api';

import { CoreFacade } from '@core/core.module';

import { COURT_EVENT_INFO_FEATURE_KEY, CourtEventInfoStateModel } from './court-event-info.state.model';
import * as actions from './court-event-info.actions';
import { NavigationFacade } from '@navigation/navigation.module';

const initialState = {
    spinner: Spinner.Empty(),
    blockedSlotDetails: BcStateContext.Empty(),
    courtBookingDetails: BcStateContext.Empty(),
    courtClassDetails: BcStateContext.Empty()
} as CourtEventInfoStateModel;

@Injectable({ providedIn: 'root' })
@State({ name: COURT_EVENT_INFO_FEATURE_KEY, defaults: initialState })
export class CourtEventInfoState {
    constructor(
        private blockedSlotService: BlockedSlotService,
        private coreFacade: CoreFacade,
        private courtBookingService: CourtBookingService,
        private navigationFacade: NavigationFacade,
        private courtClassService: CourtClassService
    ) {}

    @Action(actions.LoadBlockedSlotDetailsAction)
    loadBlockedSlotDetails(ctx: StateContext<CourtEventInfoStateModel>, action: actions.LoadBlockedSlotDetailsAction) {
        ctx.patchState({ spinner: Spinner.Loading() });
        const club = this.coreFacade.selectedClub();
        return this.blockedSlotService
            .api10ClubIdBlockedslotBlockedSlotIdInfoGet({ clubId: club.id, blockedSlotId: action.blockedSlotId })
            .pipe(
                tap(value => {
                    ctx.patchState({ blockedSlotDetails: BcStateContext.Loaded(value), spinner: Spinner.Empty() });
                }),
                catchError(err => {
                    ctx.patchState({ spinner: Spinner.Empty() });
                    throw err;
                })
            );
    }

    @Action(actions.ClearLoadedBlockedSlotDetailsAction)
    clearLoadedBlockedSlotDetails(ctx: StateContext<CourtEventInfoStateModel>) {
        ctx.patchState({ blockedSlotDetails: BcStateContext.Empty() });
    }

    @Action(actions.GotToBlockedSlotDetailsAction)
    goToBlockedSlotDetails(ctx: StateContext<CourtEventInfoStateModel>, action: actions.GotToBlockedSlotDetailsAction) {
        const path = `court-events/details/${CourtEventType.CourtBlockedSlot.toLowerCase()}/${action.blockedSlotId}`;  
        this.navigationFacade.navigate(path);
    }

    @Action(actions.LoadCourtBookingDetailsAction)
    loadCourtBookingDetails(ctx: StateContext<CourtEventInfoStateModel>, action: actions.LoadCourtBookingDetailsAction) {
        ctx.patchState({ spinner: Spinner.Loading() });
        const club = this.coreFacade.selectedClub();
        return this.courtBookingService
            .api10ClubIdCourtbookingCourtBookingIdInfoGet({
                clubId: club.id,
                courtBookingId: action.courtBookingId
            })
            .pipe(
                tap(value => {
                    ctx.patchState({ courtBookingDetails: BcStateContext.Loaded(value), spinner: Spinner.Empty() });
                }),
                catchError(err => {
                    ctx.patchState({ spinner: Spinner.Empty() });
                    throw err;
                })
            );
    }

    @Action(actions.ClearLoadedCourtBookingDetailsAction)
    clearLoadedCourtBooking(ctx: StateContext<CourtEventInfoStateModel>, action: actions.ClearLoadedCourtBookingDetailsAction) {
        ctx.patchState({ courtBookingDetails: BcStateContext.Empty(), spinner: Spinner.Empty() });
    }

    @Action(actions.GoToCourtBookingDetailsAction)
    goToCourtBookingDetails(ctx: StateContext<CourtEventInfoStateModel>, action: actions.GoToCourtBookingDetailsAction) {
        const path = `court-events/details/${CourtEventType.CourtBooking.toLowerCase()}/${action.courtBookingId}`;       
        this.navigationFacade.navigate(path);
    }

    @Action(actions.CheckInCourtBookingPlayerAction)
    checkInPlayer(ctx: StateContext<CourtEventInfoStateModel>, action: actions.CheckInCourtBookingPlayerAction) {
        ctx.patchState({ spinner: Spinner.Loading() });
        const club = this.coreFacade.selectedClub();
        return this.courtBookingService
            .api10ClubIdCourtbookingCourtBookingIdCheckinPersonIdPost({
                clubId: club.id,
                courtBookingId: action.courtBookingId,
                personId: action.playerId
            })
            .pipe(
                tap(value => {
                    ctx.patchState({ spinner: Spinner.Empty() });
                    return ctx.dispatch(new actions.LoadCourtBookingDetailsAction(action.courtBookingId));
                }),
                catchError(err => {
                    ctx.patchState({ spinner: Spinner.Empty() });
                    throw err;
                })
            );
    }

    @Action(actions.CheckInAllCourtBookingPlayersAction)
    checkInAllPlayers(ctx: StateContext<CourtEventInfoStateModel>, action: actions.CheckInAllCourtBookingPlayersAction) {
        ctx.patchState({ spinner: Spinner.Loading() });
        const club = this.coreFacade.selectedClub();
        return this.courtBookingService
            .api10ClubIdCourtbookingCourtBookingIdCheckinAllPost({
                clubId: club.id,
                courtBookingId: action.courtBookingId
            })
            .pipe(
                tap(value => {
                    ctx.patchState({ spinner: Spinner.Empty() });
                    return ctx.dispatch(new actions.LoadCourtBookingDetailsAction(action.courtBookingId));
                }),
                catchError(err => {
                    ctx.patchState({ spinner: Spinner.Empty() });
                    throw err;
                })
            );
    }


    @Action(actions.NoShowAllCourtBookingPlayersAction)
    noShowAllPlayers(ctx: StateContext<CourtEventInfoStateModel>, action: actions.NoShowAllCourtBookingPlayersAction) {
        ctx.patchState({ spinner: Spinner.Loading() });
        const club = this.coreFacade.selectedClub();
        return this.courtBookingService
            .api10ClubIdCourtbookingCourtBookingIdNoShowAllPost({
                clubId: club.id,
                courtBookingId: action.courtBookingId
            })
            .pipe(
                tap(value => {
                    ctx.patchState({ spinner: Spinner.Empty() });
                    return ctx.dispatch(new actions.LoadCourtBookingDetailsAction(action.courtBookingId));
                }),
                catchError(err => {
                    ctx.patchState({ spinner: Spinner.Empty() });
                    throw err;
                })
            );
    }

    @Action(actions.LoadCourtClassDetailsAction)
    loadCourtClassDetails(ctx: StateContext<CourtEventInfoStateModel>, action: actions.LoadCourtClassDetailsAction) {
        const club = this.coreFacade.selectedClub();
        return this.courtClassService
            .api10ClubIdCourtclassCourtClassIdInfoGet({ clubId: club.id, courtClassId: action.courtClassId })
            .pipe(
                tap(value => {
                    ctx.patchState({ courtClassDetails: BcStateContext.Loaded(value)});
                }),
                catchError(err => {
                    ctx.patchState({ courtClassDetails: BcStateContext.Error() });
                    throw err;
                })
            );
    }

    @Action(actions.ClearLoadedCourtClassDetailsAction)
    clearLoadedCourtClassDetails(ctx: StateContext<CourtEventInfoStateModel>) {
        ctx.patchState({ courtClassDetails: BcStateContext.Empty() });
    }

    @Action(actions.GoToCourtClassDetailsAction)
    goToCourtClassDetails(ctx: StateContext<CourtEventInfoStateModel>, action: actions.GoToCourtClassDetailsAction) {
        const path = `court-events/details/${CourtEventType.CourtClass.toLowerCase()}/${action.courtBookingId}`;       
        this.navigationFacade.navigate(path);
    }
}
